import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import _ from 'lodash'
import {
  AlertTriangle,
  CheckCircle,
  ClipboardCheck,
  Clock,
  Eye,
  FileDown,
  HelpCircle,
  Pencil,
  Trash2,
} from 'lucide-react'
import { observer } from 'mobx-react'
import { useContext, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import { ConfirmButton } from '@/components/ConfirmButton'
import { DataTable } from '@/components/DataTable'
import { SortableColumnHeader } from '@/components/DataTable/DataTable'
import { Badge } from '@/components/elements/badge'
import { Button } from '@/components/elements/button'
import { Input } from '@/components/elements/input'
import { Layout } from '@/components/Layout'
import { getEAPs, deleteEAP } from '@/services/eap.service'
import { UserStoreContext } from '@/stores/UserStore'
import { toast } from '@/utils/helpers'

const formatDate = (dateString) => {
  if (!dateString) return '—'
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Accounts / Emergency Action Plans
 *
 * RescueX Users, Entity Owners, and Entity Admins can manage EAPs.
 * Instructors and Members only see EAPs for their assigned jobs and get
 * a per-row indicator of when their quiz was last completed and when it's
 * next due.
 */
export const EAPs = observer(() => {
  const { entityId } = useParams()
  const navigate = useNavigate()

  const { user, isEntityOwner, isEntityAdmin } = useContext(UserStoreContext)

  const canManageEAPs = user.isXverseAdmin || isEntityOwner || isEntityAdmin
  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [sorting, setSorting] = useState([])
  const [pagination, setPagination] = useState({
    pageSize: 20,
    pageIndex: 0,
  })

  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()
  const queryKey = ['accounts', 'eaps', entityId, sorting, pagination, searchTerm]

  const { data: eaps, isPending } = useQuery({
    queryKey,
    queryFn: () =>
      getEAPs({
        entityId,
        sorting,
        pagination,
        searchTerm,
      }),
    placeholderData: keepPreviousData,
  })

  const { isPending: pendingDelete, mutate: deleteEAPMutation } = useMutation({
    mutationFn: (data) => deleteEAP({ entityId, id: data }),
    onError: (err) => handleError(err.message),
    onSuccess: () => {
      handleSuccess('Emergency Action Plan deleted successfully')
      queryClient.invalidateQueries({ queryKey })
    },
  })

  // Search shrinks the result set, so a deep page can 404 from the paginator.
  // Reset to page 0 in the same handler so React batches the state updates.
  const resetToFirstPage = () =>
    setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }))

  const debounceSearch = useRef(
    _.debounce((value) => {
      setSearchTerm(value)
      resetToFirstPage()
    }, 500),
  ).current

  const handleNavigateToPreview = (eap) => {
    navigate(`/accounts/${entityId}/eaps/${eap.id}/preview`)
  }

  const renderQuizCell = ({ row }) => {
    const { quizEnabled, quizQuestionCount, quizStatus, id } = row.original

    if (!quizEnabled) return <span className="text-gray-400">—</span>
    if (!quizQuestionCount) return <span className="text-gray-400">No quiz</span>

    const isOverdue = quizStatus?.isOverdue
    const isOpen = quizStatus?.isOpen ?? true
    const openAt = quizStatus?.openAt

    const label = isOverdue ? 'Take now' : 'Take quiz'
    const variant = isOverdue ? 'destructive' : 'outline'
    const title = isOpen ? 'Take this quiz' : `Quiz opens ${formatDate(openAt)}`

    return (
      <div className="flex flex-col items-start gap-1">
        <Button
          variant={variant}
          size="sm"
          disabled={!isOpen}
          onClick={() => navigate(`/accounts/${entityId}/eaps/${id}/quiz`)}
          title={title}
        >
          <HelpCircle className="mr-1 h-4 w-4" />
          {label}
        </Button>
        {!isOpen && !isOverdue && (
          <span className="text-muted-foreground text-xs">Opens {formatDate(openAt)}</span>
        )}
      </div>
    )
  }

  const renderActionsCell = ({ row }) => (
    <div className="flex items-center justify-end gap-1">
      <Button
        variant="ghost"
        size="sm"
        title="Preview"
        aria-label="Preview EAP"
        onClick={() => navigate(`/accounts/${entityId}/eaps/${row.original.id}/preview`)}
      >
        <Eye className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        title="Export to PDF"
        aria-label="Export EAP to PDF"
        onClick={() => handleNavigateToPreview(row.original)}
      >
        <FileDown className="h-4 w-4" />
      </Button>
      {/* Shared (org-owned) EAPs are read-only here — the org manages them. */}
      {canManageEAPs && !row.original.organization && (
        <>
          <Button
            variant="ghost"
            size="sm"
            title="View compliance"
            aria-label="View compliance"
            onClick={() => navigate(`/accounts/${entityId}/eaps/${row.original.id}/compliance`)}
          >
            <ClipboardCheck className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            title="Edit"
            aria-label="Edit EAP"
            onClick={() => navigate(`/accounts/${entityId}/eaps/${row.original.id}`)}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <ConfirmButton
            variant="ghost"
            size="sm"
            title="Delete"
            aria-label="Delete EAP"
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            confirmContent={`Are you sure you want to delete: ${row.original.name}?`}
            confirmTitle="Delete Emergency Action Plan"
            loading={pendingDelete}
            onClick={() => deleteEAPMutation(row.original.id)}
          >
            <Trash2 className="h-4 w-4" />
          </ConfirmButton>
        </>
      )}
    </div>
  )

  const renderStatusCell = ({ row }) => {
    const { quizEnabled, quizStatus, quizQuestionCount } = row.original
    if (!quizEnabled || !quizQuestionCount) return <span className="text-gray-400">—</span>
    if (!quizStatus) return <span className="text-gray-400">—</span>

    const { isOverdue, isDue, lastPassedAt, status } = quizStatus

    if (isOverdue) {
      return (
        <Badge variant="destructive" className="flex w-fit items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          Overdue
        </Badge>
      )
    }
    if (status === 'never_completed') {
      return (
        <Badge variant="warning" className="flex w-fit items-center gap-1">
          <Clock className="h-3 w-3" />
          Not yet taken
        </Badge>
      )
    }
    if (isDue) {
      return (
        <Badge variant="warning" className="flex w-fit items-center gap-1">
          <Clock className="h-3 w-3" />
          Due
        </Badge>
      )
    }
    if (lastPassedAt) {
      return (
        <Badge variant="success" className="flex w-fit items-center gap-1">
          <CheckCircle className="h-3 w-3" />
          Compliant
        </Badge>
      )
    }
    return <span className="text-gray-400">—</span>
  }

  const renderDatesCell = ({ row }) => {
    const { quizEnabled, quizStatus } = row.original
    if (!quizEnabled || !quizStatus) return <span className="text-gray-400">—</span>

    const { lastPassedAt, lastAttemptAt, nextDueDate } = quizStatus
    const completed = lastPassedAt || lastAttemptAt
    return (
      <div className="text-xs">
        <div>
          <span className="text-muted-foreground">Last:</span> {formatDate(completed)}
        </div>
        <div>
          <span className="text-muted-foreground">Due:</span> {formatDate(nextDueDate)}
        </div>
      </div>
    )
  }

  return (
    <Layout breadcrumbs={['Admin', 'Emergency Action Plans']}>
      <Layout.Heading title="Emergency Action Plans" />

      {canManageEAPs && (
        <div className="flex flex-row items-center gap-2 md:hidden">
          <Button onClick={() => navigate(`/accounts/${entityId}/eaps/new`)}>Add EAP</Button>
        </div>
      )}

      <div className="flex w-full flex-row items-center justify-between">
        <div className="mt-2 mb-2 flex w-full flex-col items-center gap-4 py-1 md:flex-row">
          <Input
            className="w-full md:w-64"
            placeholder="Search for EAP"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              debounceSearch(e.target.value)
            }}
          />

          {searchTerm && (
            <Button
              className="hidden md:block"
              variant="outline"
              onClick={() => {
                setSearch('')
                setSearchTerm('')
                resetToFirstPage()
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>

        {canManageEAPs && (
          <div className="hidden flex-row items-center gap-2 md:flex">
            <Button onClick={() => navigate(`/accounts/${entityId}/eaps/new`)}>Add EAP</Button>
          </div>
        )}
      </div>

      <DataTable
        rowClassName={({ row }) => {
          const status = row.original.quizStatus
          if (!status) return ''
          // TableRow applies `even:bg-muted` by default, which has higher
          // specificity than a plain bg-* class, so the highlight wouldn't
          // show up on every other row. Echo the bg into the even: variant
          // so overdue/due rows always read clearly.
          if (status.isOverdue) {
            return 'bg-red-50 even:bg-red-50 hover:bg-red-100 even:hover:bg-red-100'
          }
          if (status.isDue) {
            return 'bg-yellow-50 even:bg-yellow-50 hover:bg-yellow-100 even:hover:bg-yellow-100'
          }
          return ''
        }}
        columns={[
          {
            accessorKey: 'name',
            id: 'name',
            header: ({ column }) => <SortableColumnHeader column={column} label="Name" />,
            cell: ({ row }) => {
              const isShared = !!row.original.organization
              return (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="text-action cursor-pointer text-left hover:underline"
                    onClick={() => {
                      // Shared EAPs are read-only: managers preview rather than edit.
                      if (canManageEAPs && !isShared) {
                        navigate(`/accounts/${entityId}/eaps/${row.original.id}`)
                      } else {
                        navigate(`/accounts/${entityId}/eaps/${row.original.id}/preview`)
                      }
                    }}
                  >
                    {row.original.name}
                  </button>
                  {isShared && <Badge variant="secondary">Shared</Badge>}
                </div>
              )
            },
          },
          {
            accessorKey: 'jobs',
            id: 'jobs',
            header: 'Associated Jobs',
            cell: ({ row }) => {
              const jobs = row.original.jobs || []
              if (jobs.length === 0) return 'All jobs'
              return jobs.map((j) => j.title).join(', ')
            },
          },
          {
            id: 'quizStatus',
            header: 'Status',
            cell: renderStatusCell,
            size: 180,
          },
          {
            id: 'quizDates',
            header: 'Last / Due',
            cell: renderDatesCell,
            size: 180,
          },
          {
            accessorKey: 'modifiedAt',
            id: 'modifiedAt',
            header: ({ column }) => <SortableColumnHeader column={column} label="Last Updated" />,
            cell: ({ row }) => {
              const date = row.original.modifiedAt || row.original.updatedAt
              if (!date) return '-'
              return new Date(date).toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              })
            },
            size: 180,
          },
          {
            id: 'quiz',
            header: 'Quiz',
            cell: renderQuizCell,
            size: 150,
          },
          {
            id: 'actions',
            header: '',
            cell: renderActionsCell,
            size: canManageEAPs ? 220 : 100,
          },
        ]}
        data={eaps?.results || []}
        loading={isPending}
        pagination={pagination}
        rowCount={eaps?.count || 0}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
      />
    </Layout>
  )
})
